import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider
} from '@mui/material';
import {
  Checkroom as CheckroomIcon,
  Event as EventIcon,
  Palette as PaletteIcon,
  Straighten as SizeIcon,
  Edit as EditIcon,
  Info as InfoIcon
} from '@mui/icons-material';

interface ClothingAssignment {
  id: string;
  guestId: string;
  eventId: string;
  itemType: string;
  size: string;
  color?: string;
  pickupInstructions?: string;
  status: string;
  eventName?: string;
  eventDate?: string;
}

interface ChangeRequest {
  id: string;
  assignmentId?: string;
  requestType: 'event_change' | 'size_change';
  status: 'pending' | 'approved' | 'denied';
  createdAt: string;
}

interface GuestClothingViewProps {
  clothingAssignments: ClothingAssignment[];
  pendingRequests?: ChangeRequest[];
  onRefresh: () => void;
}

interface SizeChangeRequest {
  assignmentId: string;
  newSize: string;
  reason?: string;
}

const getAvailableSizes = (itemType: string) => {
  if (itemType === 'African Style Cargo Pants') {
    return [
      'Regular Length',
      'Regular Length & Extra Wide Waist',
      'Long',
      'Extra Long',
      'Extra Long & Extra Wide Waist'
    ];
  }
  if (itemType === 'Hat') {
    return ['One Size'];
  }
  // Standard sizes for T-Shirts and Golf Polo Shirts
  return ['XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', '3XL'];
};

export const GuestClothingView: React.FC<GuestClothingViewProps> = ({ clothingAssignments, pendingRequests = [], onRefresh }) => {
  const [changeDialogOpen, setChangeDialogOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<ClothingAssignment | null>(null);
  const [newSize, setNewSize] = useState('');

  // Helper to check if assignment has pending request
  const hasPendingRequest = (assignmentId: string) => {
    return pendingRequests.some(
      req => req.assignmentId === assignmentId && req.requestType === 'size_change' && req.status === 'pending'
    );
  };
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Date TBD';
    // Parse YYYY-MM-DD as local date to avoid timezone shifts
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'assigned':
        return 'success';
      case 'pending_change':
        return 'warning';
      case 'ready_for_pickup':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'assigned':
        return 'Assigned';
      case 'pending_change':
        return 'Change Pending';
      case 'ready_for_pickup':
        return 'Ready for Pickup';
      default:
        return status;
    }
  };

  const handleRequestSizeChange = (assignment: ClothingAssignment) => {
    setSelectedAssignment(assignment);
    setNewSize(assignment.size);
    setReason('');
    setError(null);
    setChangeDialogOpen(true);
  };

  const handleSubmitSizeChange = async () => {
    if (!selectedAssignment) return;

    try {
      setSubmitting(true);
      setError(null);

      const requestData: SizeChangeRequest = {
        assignmentId: selectedAssignment.id,
        newSize,
        reason: reason.trim() || undefined
      };

      // Use direct API Gateway URL to bypass CloudFront
      const apiUrl = process.env.NODE_ENV === 'production' 
        ? 'https://3amqzkaklb.execute-api.us-east-1.amazonaws.com/prod/api/clothing/request-size-change'
        : 'http://localhost:3001/api/clothing/request-size-change';
      const authToken = localStorage.getItem('authToken');
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken && { 'Authorization': `Bearer ${authToken}` })
        },
        credentials: 'include',
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to submit size change request');
      }

      setChangeDialogOpen(false);
      onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseDialog = () => {
    setChangeDialogOpen(false);
    setSelectedAssignment(null);
    setNewSize('');
    setReason('');
    setError(null);
  };

  // Group assignments by event
  const assignmentsByEvent = clothingAssignments.reduce((acc, assignment) => {
    const eventKey = assignment.eventName || `Event ${assignment.eventId}`;
    if (!acc[eventKey]) {
      acc[eventKey] = [];
    }
    acc[eventKey].push(assignment);
    return acc;
  }, {} as Record<string, ClothingAssignment[]>);

  // Sort events by date (earliest first)
  const eventKeys = Object.keys(assignmentsByEvent).sort((a, b) => {
    const dateA = assignmentsByEvent[a][0]?.eventDate;
    const dateB = assignmentsByEvent[b][0]?.eventDate;
    
    // If both have dates, sort by date
    if (dateA && dateB) {
      return dateA.localeCompare(dateB);
    }
    // Events without dates go to the end
    if (!dateA && dateB) return 1;
    if (dateA && !dateB) return -1;
    // If neither has a date, sort alphabetically by name
    return a.localeCompare(b);
  });

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <CheckroomIcon sx={{ mr: 1 }} />
        My Clothing Assignments
      </Typography>

      {clothingAssignments.length === 0 ? (
        <Alert severity="info">
          No clothing assignments found. Check back later for updates.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {eventKeys.map(eventName => (
            <Grid item xs={12} key={eventName}>
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                <EventIcon sx={{ mr: 1, fontSize: 20 }} />
                {eventName}
                {assignmentsByEvent[eventName][0]?.eventDate && (
                  <Chip 
                    label={formatDate(assignmentsByEvent[eventName][0].eventDate)}
                    size="small"
                    sx={{ ml: 2 }}
                  />
                )}
              </Typography>
              
              <Grid container spacing={2}>
                {assignmentsByEvent[eventName].map(assignment => (
                  <Grid item xs={12} sm={6} md={4} key={assignment.id}>
                    <Card sx={{ height: '100%' }}>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                          <Typography variant="h6" component="div">
                            {assignment.itemType}
                          </Typography>
                          <Chip
                            label={getStatusLabel(assignment.status)}
                            color={getStatusColor(assignment.status) as any}
                            size="small"
                          />
                        </Box>

                        <Box display="flex" alignItems="center" mb={1}>
                          <SizeIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            Size: {assignment.size}
                          </Typography>
                        </Box>

                        {assignment.color && (
                          <Box display="flex" alignItems="center" mb={1}>
                            <PaletteIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              Color: {assignment.color}
                            </Typography>
                          </Box>
                        )}

                        {assignment.pickupInstructions && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                              <InfoIcon sx={{ mr: 1, fontSize: 16, color: 'info.main' }} />
                              Pickup Instructions:
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 3 }}>
                              {assignment.pickupInstructions}
                            </Typography>
                          </Box>
                        )}

                        {!hasPendingRequest(assignment.id) && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => handleRequestSizeChange(assignment)}
                            fullWidth
                            sx={{ mt: 2 }}
                          >
                            Request Size Change
                          </Button>
                        )}

                        {hasPendingRequest(assignment.id) && (
                          <Alert severity="info" sx={{ mt: 2 }}>
                            Your size change request is pending admin approval
                          </Alert>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              
              {eventName !== eventKeys[eventKeys.length - 1] && (
                <Divider sx={{ my: 3 }} />
              )}
            </Grid>
          ))}
        </Grid>
      )}

      {/* Size Change Request Dialog */}
      <Dialog open={changeDialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Request Size Change
        </DialogTitle>
        <DialogContent>
          {selectedAssignment && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Item: {selectedAssignment.itemType}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Current Size: {selectedAssignment.size}
              </Typography>

              <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
                <InputLabel>New Size</InputLabel>
                <Select
                  value={newSize}
                  label="New Size"
                  onChange={(e) => setNewSize(e.target.value)}
                >
                  {getAvailableSizes(selectedAssignment.itemType).map(size => (
                    <MenuItem key={size} value={size}>
                      {size}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Reason (optional)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Please provide a reason for the size change..."
                sx={{ mb: 2 }}
              />

              <Alert severity="info" sx={{ mb: 2 }}>
                Size changes are subject to availability and host approval.
              </Alert>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitSizeChange} 
            variant="contained"
            disabled={submitting || newSize === selectedAssignment?.size}
          >
            {submitting ? 'Submitting...' : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};